import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

// event kind -> chip colour
const KIND_STYLE = {
  booking: "bg-amber-100 text-amber-800 border-amber-200",
  eta: "bg-blue-100 text-blue-800 border-blue-200",
  inhouse: "bg-violet-100 text-violet-800 border-violet-200",
  shipment: "bg-emerald-100 text-emerald-800 border-emerald-200",
  overdue: "bg-rose-100 text-rose-800 border-rose-200",
};

const LEGEND = [
  { label: "Booking", cls: KIND_STYLE.booking },
  { label: "Arrival (ETA)", cls: KIND_STYLE.eta },
  { label: "In-house", cls: KIND_STYLE.inhouse },
  { label: "Shipment", cls: KIND_STYLE.shipment },
  { label: "Overdue", cls: KIND_STYLE.overdue },
];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Calendar() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayModal, setDayModal] = useState(null); // ymd string of expanded day
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar");
      setEvents(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      (map[ev.date] = map[ev.date] || []).push(ev);
    }
    return map;
  }, [events]);

  // build 6x7 grid for the current month
  const cells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay()); // back to Sunday
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  }, [cursor]);

  const todayStr = ymd(new Date());

  const upcoming = useMemo(() => {
    const t = todayStr;
    return events.filter((e) => e.date >= t).slice(0, 12);
  }, [events, todayStr]);

  const openEvent = (ev) => navigate(`/orders/${ev.order_id}?section=${ev.section}`);

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNav />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading flex items-center gap-2 text-3xl font-extrabold tracking-tight text-slate-900">
              <CalendarDays className="h-8 w-8 text-blue-600" /> Calendar
            </h1>
            <p className="mt-1 text-lg text-slate-500">Upcoming bookings, arrivals and shipments from your orders</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="cal-prev-btn" variant="outline" className="h-11 w-11 p-0" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span data-testid="cal-month-label" className="min-w-48 text-center font-heading text-xl font-bold text-slate-800">{monthLabel}</span>
            <Button data-testid="cal-next-btn" variant="outline" className="h-11 w-11 p-0" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <Button data-testid="cal-today-btn" variant="outline" className="h-11 px-5" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }}>
              Today
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-5 flex flex-wrap gap-3">
          {LEGEND.map((l) => (
            <span key={l.label} className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold", l.cls)}>
              {l.label}
            </span>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            {/* Month grid */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {WEEKDAYS.map((w) => (
                  <div key={w} className="px-2 py-3 text-center text-sm font-bold uppercase tracking-wide text-slate-500">{w}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {cells.map((d, i) => {
                  const ds = ymd(d);
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const dayEvents = eventsByDate[ds] || [];
                  const isToday = ds === todayStr;
                  return (
                    <div
                      key={ds}
                      data-testid={`cal-day-${ds}`}
                      className={cn(
                        "min-h-28 border-b border-r border-slate-100 p-1.5 align-top",
                        !inMonth && "bg-slate-50/60",
                        i % 7 === 6 && "border-r-0"
                      )}
                    >
                      <div className={cn(
                        "mb-1 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold",
                        isToday ? "bg-blue-600 text-white" : inMonth ? "text-slate-700" : "text-slate-400"
                      )}>
                        {d.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((ev) => (
                          <button
                            key={ev.id}
                            data-testid={`cal-event-${ev.id}`}
                            onClick={() => openEvent(ev)}
                            title={`${ev.label} — Order ${ev.order_style}`}
                            className={cn(
                              "block w-full truncate rounded-md border px-1.5 py-0.5 text-left text-xs font-semibold transition-transform hover:scale-[1.02]",
                              ev.overdue ? KIND_STYLE.overdue : KIND_STYLE[ev.kind]
                            )}
                          >
                            {ev.label} · {ev.order_style}
                          </button>
                        ))}
                        {dayEvents.length > 3 && (
                          <button
                            data-testid={`cal-more-${ds}`}
                            onClick={() => setDayModal(ds)}
                            className="block w-full rounded-md px-1.5 text-left text-xs font-semibold text-blue-600 hover:bg-blue-50 hover:underline"
                          >
                            +{dayEvents.length - 3} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming list */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-slate-800">Upcoming</h3>
              <p className="text-sm text-slate-400">Next deadlines & milestones</p>
              <div className="mt-4 space-y-2">
                {upcoming.length === 0 && <p className="py-6 text-center text-base text-slate-400">No upcoming events.</p>}
                {upcoming.map((ev) => (
                  <button
                    key={ev.id}
                    data-testid={`cal-upcoming-${ev.id}`}
                    onClick={() => openEvent(ev)}
                    className="flex w-full items-center gap-3 rounded-lg border border-slate-100 p-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <span className={cn("flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-lg border text-center", ev.overdue ? KIND_STYLE.overdue : KIND_STYLE[ev.kind])}>
                      <span className="text-xs font-bold uppercase leading-none">{MONTHS[Number(ev.date.slice(5, 7)) - 1].slice(0, 3)}</span>
                      <span className="text-base font-extrabold leading-tight">{ev.date.slice(8, 10)}</span>
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-slate-800">{ev.label}</p>
                      <p className="truncate text-sm text-slate-500">Order {ev.order_style}{ev.buyer ? ` · ${ev.buyer}` : ""}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All events for a single day */}
        <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                {dayModal && `${MONTHS[Number(dayModal.slice(5, 7)) - 1]} ${Number(dayModal.slice(8, 10))}, ${dayModal.slice(0, 4)}`}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto py-1">
              {(eventsByDate[dayModal] || []).map((ev) => (
                <button
                  key={ev.id}
                  data-testid={`cal-day-event-${ev.id}`}
                  onClick={() => { setDayModal(null); openEvent(ev); }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg border p-3 text-left transition-transform hover:scale-[1.01]",
                    ev.overdue ? KIND_STYLE.overdue : KIND_STYLE[ev.kind]
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-base font-bold">{ev.label}</p>
                    <p className="truncate text-sm font-medium opacity-80">Order {ev.order_style}{ev.buyer ? ` · ${ev.buyer}` : ""}</p>
                  </div>
                </button>
              ))}
              {dayModal && (eventsByDate[dayModal] || []).length === 0 && (
                <p className="py-6 text-center text-base text-slate-400">No events.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
